// Layer 3: the closed-month EBITDA bridge, Budget (M5 target) to actual.
// The bridge rows and waterfall live in `shared/ebitda-bridge.derive.ts`
// (polish.P7), where Cost & revenue reads the same function.
import { gmMoney, gmAmount, gmSignedAmount } from './GmMoney.transform';
import { ebitdaBridgeRows } from '@/versions/v1/shared/ebitda-bridge.derive';
import { COST_PERIODS, type CostPeriod } from '../cost/Scene.transform';
export {
  ebitdaBridgeActual,
  ebitdaBridgeRows,
  ebitdaWaterfall,
  largestRemainderHundredths,
  type EbitdaBridgeRow,
  type EbitdaWaterfallBar,
} from '@/versions/v1/shared/ebitda-bridge.derive';

/** The closed window this insight reads: `gmMoney()`'s default period, the one
 *  the Superintendent's revenue table prints (`1–7 Jun · US$K`). */
const BRIDGE_PERIOD: CostPeriod = 'month';

/** The headline's period word, read from the period vocabulary the view uses
 *  (`Month to date` → `month`), never typed as a second copy fact. */
function periodWord(): string {
  const label = COST_PERIODS.find((period) => period.id === BRIDGE_PERIOD)?.label ?? 'Month to date';
  return label.split(' ')[0]!.toLowerCase();
}

/** A bridge step's figure-free driver word: the label's head, without the fact
 *  it appends (`Volume · +2,321 dst` carries a figure) or the comparator suffix
 *  the table's own column header already names (`Recovery vs budget`). */
function driverWord(label: string): string {
  return label.split(' · ')[0]!.replace(/ vs budget$/, '');
}

/**
 * The Engine insight on the Superintendent's revenue & operating-EBITDA table.
 * The headline carries no figure — the table row above it prints the EBITDA —
 * and names the bridge's own drivers by the sign of their effect: the steps that
 * add carry the period, the steps that take give some back. The body keeps its
 * figures (one signed amount per non-zero step).
 */
export function ebitdaInsightCopy(): {headline:string;body:string} {
  const g = gmMoney();
  const steps = ebitdaBridgeRows().filter((row) => !row.bold && row.value !== 0);
  // Largest tailwind first; the smallest drag first, so the two that give back
  // are read before the biggest one (the comp's headline order).
  const carry = steps.filter((row) => row.value > 0).sort((a, b) => b.value - a.value);
  const giveBack = steps.filter((row) => row.value < 0).sort((a, b) => b.value - a.value);
  const names = (rows: readonly { label: string }[]) =>
    rows.map((row) => driverWord(row.label).toLowerCase()).join(' and ');
  const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
  const headline = carry.length > 0 && giveBack.length > 0
    ? `${sentence(names(carry))} carry the ${periodWord()}; ${names(giveBack)} give some back`
    : carry.length > 0
      ? `${sentence(names(carry))} carry the ${periodWord()}`
      : `${sentence(names(giveBack))} give some back`;
  const effects = g.bridge.filter(r=>r.value!==0).map(r=>`${r.label}: ${gmSignedAmount(r.value)}`).join(' · ');
  return {
    headline,
    body:`Budget is Month-4 recovery at 1,440 st/d. ${effects}. vs design potential ${gmAmount(g.mtd.ebitda.budget)} (${gmSignedAmount(g.mtd.ebitda.actual-g.mtd.ebitda.budget)}).`,
  };
}
